import React, { useState, useEffect } from 'react';
import { supabase } from '../../../lib/supabase';
import { 
  Edit3, 
  Search, 
  Plus, 
  Trash2, 
  Eye,
  EyeOff,
  Globe,
  Type,
  Image,
  Save,
  RefreshCw
} from 'lucide-react';

const ContentManagement = ({ onStatsUpdate }) => {
  const [contents, setContents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterSection, setFilterSection] = useState('all');
  const [filterType, setFilterType] = useState('all');
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingContent, setEditingContent] = useState(null);

  // Content section options (based on frontend_content table)
  const sectionOptions = [
    'homepage.hero_section',
    'homepage.featured_properties', 
    'homepage.services',
    'homepage.testimonials',
    'about.mission',
    'about.team',
    'services.legal',
    'services.consultation',
    'contact.info'
  ];

  // Content type options
  const typeOptions = [
    { value: 'hero', label: 'Hero Section' },
    { value: 'text', label: 'Texte' },
    { value: 'image', label: 'Image' },
    { value: 'banner', label: 'Bannière' },
    { value: 'news', label: 'Actualité' },
    { value: 'testimonial', label: 'Témoignage' },
    { value: 'properties', label: 'Propriétés' }
  ];

  // Fetch all content
  const fetchContents = async () => {
    try {
      setLoading(true);
      let query = supabase?.from('frontend_content')?.select(`
        *,
        created_by_user:user_profiles!created_by(full_name),
        updated_by_user:user_profiles!updated_by(full_name)
      `)?.order('display_order', { ascending: true });

      // Apply filters
      if (filterSection !== 'all') {
        query = query?.eq('page_section', filterSection);
      }
      if (filterType !== 'all') {
        query = query?.eq('section_type', filterType);
      }

      const { data, error } = await query;
      if (error) throw error;

      setContents(data || []);
    } catch (error) {
      console.error('Error fetching content:', error);
      alert('Erreur lors du chargement du contenu');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchContents();
  }, [filterSection, filterType]);

  // Filter contents based on search term
  const filteredContents = contents?.filter(content => 
    content?.title?.toLowerCase()?.includes(searchTerm?.toLowerCase()) ||
    content?.content?.toLowerCase()?.includes(searchTerm?.toLowerCase()) ||
    content?.page_section?.toLowerCase()?.includes(searchTerm?.toLowerCase())
  );

  // Handle content save
  const handleSaveContent = async (contentData) => {
    try {
      if (editingContent) {
        // Update existing content
        const { error } = await supabase?.from('frontend_content')?.update({
          ...contentData,
          updated_at: new Date()?.toISOString()
        })?.eq('id', editingContent?.id);
        if (error) throw error;
      } else {
        // Create new content
        const { error } = await supabase?.from('frontend_content')?.insert([contentData]);
        if (error) throw error;
      }

      setShowAddModal(false);
      setEditingContent(null);
      fetchContents();
      onStatsUpdate?.();
    } catch (error) {
      console.error('Error saving content:', error);
      alert('Erreur lors de l\'enregistrement du contenu');
    }
  };

  // Handle content deletion
  const handleDeleteContent = async (contentId) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer ce contenu ?')) {
      return;
    }

    try {
      const { error } = await supabase?.from('frontend_content')?.delete()?.eq('id', contentId);
      if (error) throw error;

      fetchContents();
      onStatsUpdate?.();
    } catch (error) {
      console.error('Error deleting content:', error);
      alert('Erreur lors de la suppression du contenu');
    }
  };

  // Toggle published status
  const handleTogglePublished = async (contentId, isPublished) => {
    try {
      const { error } = await supabase?.from('frontend_content')?.update({ 
        is_published: !isPublished,
        updated_at: new Date()?.toISOString()
      })?.eq('id', contentId);
      if (error) throw error;

      fetchContents();
      onStatsUpdate?.();
    } catch (error) {
      console.error('Error toggling publish status:', error);
      alert('Erreur lors de la mise à jour du statut');
    }
  };

  // Update display order
  const handleOrderChange = async (contentId, newOrder) => {
    try {
      const { error } = await supabase?.from('frontend_content')?.update({ 
        display_order: parseInt(newOrder),
        updated_at: new Date()?.toISOString()
      })?.eq('id', contentId);
      if (error) throw error;

      fetchContents();
    } catch (error) {
      console.error('Error updating order:', error);
      alert('Erreur lors de la mise à jour de l\'ordre');
    }
  };

  // Get type label
  const getTypeLabel = (type) => {
    const option = typeOptions?.find(opt => opt?.value === type);
    return option?.label || type;
  };

  // Truncate text for display
  const truncateText = (text, maxLength = 100) => {
    if (!text || text?.length <= maxLength) return text;
    return text?.substring(0, maxLength) + '...';
  };

  if (loading) {
    return (
      <div className="space-y-4">
        {[...Array(5)]?.map((_, i) => (
          <div key={i} className="bg-white p-6 rounded-lg shadow-sm animate-pulse">
            <div className="h-4 bg-gray-200 rounded w-1/4 mb-4"></div>
            <div className="h-3 bg-gray-200 rounded w-3/4 mb-2"></div>
            <div className="h-3 bg-gray-200 rounded w-1/2"></div>
          </div>
        ))}
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header with Actions */}
      <div className="bg-white p-6 rounded-lg shadow-sm">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between space-y-4 lg:space-y-0">
          <div>
            <h3 className="text-lg font-semibold text-gray-900 flex items-center">
              <Edit3 className="w-5 h-5 mr-2 text-blue-600" />
              Gestion du Contenu du Site
            </h3>
            <p className="text-sm text-gray-600 mt-1">
              {filteredContents?.length} contenu(s) trouvé(s)
            </p>
          </div>
          
          <div className="flex flex-col sm:flex-row space-y-2 sm:space-y-0 sm:space-x-4">
            {/* Search */}
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
              <input
                type="text"
                placeholder="Rechercher contenu..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e?.target?.value)}
                className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
            
            {/* Refresh Button */}
            <button
              onClick={fetchContents}
              className="flex items-center space-x-2 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
            >
              <RefreshCw className="w-4 h-4" />
              <span>Actualiser</span>
            </button>
            
            {/* Add Content Button */}
            <button
              onClick={() => setShowAddModal(true)}
              className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              <Plus className="w-4 h-4" />
              <span>Nouveau Contenu</span>
            </button>
          </div>
        </div>

        {/* Filters */}
        <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Section</label>
            <select
              value={filterSection}
              onChange={(e) => setFilterSection(e?.target?.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="all">Toutes les sections</option>
              {sectionOptions?.map(section => (
                <option key={section} value={section}>{section}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Type</label>
            <select
              value={filterType}
              onChange={(e) => setFilterType(e?.target?.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="all">Tous les types</option>
              {typeOptions?.map(option => (
                <option key={option?.value} value={option?.value}>{option?.label}</option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* Content List */}
      <div className="bg-white rounded-lg shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Ordre
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Contenu
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Section
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Type
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Statut
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Dernière Modif.
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredContents?.map((content) => (
                <tr key={content?.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <input
                      type="number"
                      min="1"
                      value={content?.display_order}
                      onChange={(e) => handleOrderChange(content?.id, e?.target?.value)}
                      className="w-16 px-2 py-1 border border-gray-300 rounded text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </td>
                  
                  <td className="px-6 py-4">
                    <div>
                      <div className="text-sm font-medium text-gray-900 mb-1">
                        {content?.title}
                      </div>
                      <div className="text-sm text-gray-600">
                        {truncateText(content?.content)}
                      </div>
                      {content?.content_fr && (
                        <div className="text-xs text-blue-600 mt-1 flex items-center">
                          <Globe className="w-3 h-3 mr-1" />
                          Version FR disponible
                        </div>
                      )}
                    </div>
                  </td>
                  
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900">{content?.page_section}</div>
                  </td>
                  
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-gray-100 text-gray-800">
                      {getTypeLabel(content?.section_type)}
                    </div>
                  </td>
                  
                  <td className="px-6 py-4 whitespace-nowrap">
                    <button
                      onClick={() => handleTogglePublished(content?.id, content?.is_published)}
                      className={`inline-flex items-center px-2 py-1 text-xs font-semibold rounded-full ${
                        content?.is_published
                          ? 'bg-green-100 text-green-800' :'bg-red-100 text-red-800'
                      }`}
                    >
                      {content?.is_published ? (
                        <>
                          <Eye className="w-3 h-3 mr-1" />
                          Publié
                        </>
                      ) : (
                        <>
                          <EyeOff className="w-3 h-3 mr-1" />
                          Brouillon
                        </>
                      )}
                    </button>
                  </td>
                  
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900">
                      {new Date(content?.updated_at)?.toLocaleDateString('fr-FR')}
                    </div>
                    {content?.updated_by_user && (
                      <div className="text-xs text-gray-500">
                        par {content?.updated_by_user?.full_name}
                      </div>
                    )}
                  </td>
                  
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                    <div className="flex items-center justify-end space-x-2">
                      <button
                        onClick={() => {
                          setEditingContent(content);
                          setShowAddModal(true);
                        }}
                        className="text-blue-600 hover:text-blue-900 p-1 rounded hover:bg-blue-50"
                      >
                        <Edit3 className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleDeleteContent(content?.id)}
                        className="text-red-600 hover:text-red-900 p-1 rounded hover:bg-red-50"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          
          {filteredContents?.length === 0 && (
            <div className="text-center py-12">
              <Edit3 className="w-12 h-12 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-600">Aucun contenu trouvé</p>
              <button
                onClick={() => setShowAddModal(true)}
                className="mt-4 flex items-center space-x-2 mx-auto px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                <Plus className="w-4 h-4" />
                <span>Ajouter du contenu</span>
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Add/Edit Content Modal */}
      {showAddModal && (
        <ContentModal
          content={editingContent}
          sectionOptions={sectionOptions}
          typeOptions={typeOptions}
          onSave={handleSaveContent}
          onClose={() => {
            setShowAddModal(false);
            setEditingContent(null);
          }}
        />
      )}
    </div>
  );
};

// Content Modal Component
const ContentModal = ({ content, sectionOptions, typeOptions, onSave, onClose }) => {
  const [formData, setFormData] = useState({
    title: content?.title || '',
    content: content?.content || '',
    content_fr: content?.content_fr || '',
    page_section: content?.page_section || sectionOptions?.[0] || '',
    section_type: content?.section_type || 'text',
    display_order: content?.display_order || 1,
    is_published: content?.is_published ?? true,
    metadata: content?.metadata || {}
  });

  const handleSubmit = (e) => {
    e?.preventDefault();
    onSave(formData);
  };

  const getTypeLabel = (type) => {
    const option = typeOptions?.find(opt => opt?.value === type);
    return option?.label || type;
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" onClick={onClose}></div>

        <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-4xl sm:w-full">
          <form onSubmit={handleSubmit}>
            <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
              <div className="sm:flex sm:items-start">
                <div className="mt-3 text-center sm:mt-0 sm:text-left w-full">
                  <h3 className="text-lg leading-6 font-medium text-gray-900 mb-6">
                    {content ? 'Modifier le Contenu' : 'Ajouter du Contenu'}
                  </h3>
                  
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    {/* Left Column */}
                    <div className="space-y-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Titre *
                        </label>
                        <input
                          type="text"
                          required
                          value={formData?.title}
                          onChange={(e) => setFormData({...formData, title: e?.target?.value})}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Section *
                        </label>
                        <select
                          value={formData?.page_section}
                          onChange={(e) => setFormData({...formData, page_section: e?.target?.value})}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        >
                          {sectionOptions?.map(section => (
                            <option key={section} value={section}>{section}</option>
                          ))}
                        </select>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Type *
                        </label>
                        <select
                          value={formData?.section_type}
                          onChange={(e) => setFormData({...formData, section_type: e?.target?.value})}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        >
                          {typeOptions?.map(option => (
                            <option key={option?.value} value={option?.value}>
                              {option?.label}
                            </option>
                          ))}
                        </select>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Ordre d'affichage
                        </label>
                        <input
                          type="number"
                          min="1"
                          value={formData?.display_order}
                          onChange={(e) => setFormData({...formData, display_order: parseInt(e?.target?.value) || 1})}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        />
                      </div>

                      <div className="flex items-center">
                        <input
                          type="checkbox"
                          id="is_published"
                          checked={formData?.is_published}
                          onChange={(e) => setFormData({...formData, is_published: e?.target?.checked})}
                          className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                        />
                        <label htmlFor="is_published" className="ml-2 block text-sm text-gray-900">
                          Publier immédiatement
                        </label>
                      </div>
                    </div>

                    {/* Right Column */}
                    <div className="space-y-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Contenu (Français) *
                        </label>
                        <textarea
                          required
                          rows={8}
                          value={formData?.content}
                          onChange={(e) => setFormData({...formData, content: e?.target?.value})}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                          placeholder="Contenu principal en français..."
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1 flex items-center">
                          <Globe className="w-4 h-4 mr-1" />
                          Version Française (Optionnelle)
                        </label>
                        <textarea
                          rows={8}
                          value={formData?.content_fr}
                          onChange={(e) => setFormData({...formData, content_fr: e?.target?.value})}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                          placeholder="Version française du contenu..."
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
              <button
                type="submit"
                className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-blue-600 text-base font-medium text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:ml-3 sm:w-auto sm:text-sm"
              >
                <Save className="w-4 h-4 mr-2" />
                {content ? 'Modifier' : 'Ajouter'}
              </button>
              <button
                type="button"
                onClick={onClose}
                className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
              >
                Annuler
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default ContentManagement;